import React, { useState, useEffect } from 'react';

const OrderCard = ({ order, onUpdateStatus, isDarkMode }) => {
    const styles = getStyles(isDarkMode, order.status);
    const [timeLeft, setTimeLeft] = useState(null);

    useEffect(() => {
        if (order.status === 'PREPARING' && order.estimatedPrepTime) {
            const calculateTimeLeft = () => {
                // Assuming createdAt or updatedAt is the start of prep? 
                // Usually updated_at when moved to PREPARING. 
                // But for simplicity let's use createdAt + prepTime? 
                // Ideally backend records 'prep_start_time'. 
                // fallback: Use createdAt for now.
                const startTime = new Date(order.updatedAt || order.createdAt).getTime(); // updatedAt should update on status change
                const durationMs = order.estimatedPrepTime * 60 * 1000;
                const endTime = startTime + durationMs;
                const now = new Date().getTime();
                const diff = endTime - now;
                return Math.max(0, diff);
            };

            setTimeLeft(calculateTimeLeft());
            const interval = setInterval(() => {
                setTimeLeft(calculateTimeLeft());
            }, 60000); // Update every minute

            return () => clearInterval(interval);
        }
    }, [order.status, order.estimatedPrepTime, order.updatedAt, order.createdAt]);

    const handleReject = () => {
        const reason = window.prompt("Please enter a reason for rejection:");
        if (reason) {
            onUpdateStatus(order.id, 'REJECTED', reason);
        }
    };

    const handleAction = (status) => {
        onUpdateStatus(order.id, status);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    const formatTimeLeft = (ms) => {
        if (!ms) return '';
        const mins = Math.ceil(ms / 60000);
        return `${mins} min`;
    };

    return (
        <div style={styles.card} className="order-card">
            <div style={styles.header}>
                <div style={styles.orderIdGroup}>
                    <span style={styles.hash}>#</span>
                    <span style={styles.orderNumber}>{order.orderNumber}</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={styles.time}>
                        {(() => {
                            try {
                                return new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            } catch (e) {
                                return 'Invalid Time';
                            }
                        })()}
                    </div>
                </div>
            </div>

            <div style={styles.customerRow}>
                <span style={styles.customerName}>{order.customerName || 'Guest'}</span>
                {order.customerPhone && <span style={styles.customerPhone}>• {order.customerPhone}</span>}
            </div>
            {order.deliveryAddress && (
                <div style={styles.addressRow}>
                    📍 {(() => {
                        try {
                            const addr = typeof order.deliveryAddress === 'string' && order.deliveryAddress.trim().startsWith('{')
                                ? JSON.parse(order.deliveryAddress)
                                : order.deliveryAddress;

                            return typeof addr === 'object' && addr !== null
                                ? (addr.formattedAddress || addr.label || JSON.stringify(addr))
                                : addr;
                        } catch (e) {
                            return order.deliveryAddress;
                        }
                    })()}
                </div>
            )}

            <div style={styles.badges}>
                <span style={styles.typeTag}>{order.orderType}</span>
                <span style={styles.statusTag}>{order.status}</span>
                {order.paymentStatus && (
                    <span style={{
                        ...styles.paymentTag,
                        backgroundColor: order.paymentStatus === 'PAID' ? '#dff9fb' : '#ffefc5',
                        color: order.paymentStatus === 'PAID' ? '#22a6b3' : '#f0932b'
                    }}>
                        {order.paymentStatus}
                    </span>
                )}
            </div>

            {order.status === 'PREPARING' && timeLeft !== null && (
                <div style={styles.etaContainer}>
                    <span style={styles.etaLabel}>ETA:</span>
                    <span style={styles.etaValue}>{formatTimeLeft(timeLeft)}</span>
                </div>
            )}

            <div style={styles.itemsContainer}>
                {order.items.map((item, index) => (
                    <div key={index} style={styles.itemRow}>
                        <div style={styles.quantityBadge}>{item.quantity}x</div>
                        <div style={styles.itemDetails}>
                            <span style={styles.itemName}>{item.menuItemName}</span>
                            {item.customizations && Object.keys(item.customizations).length > 0 && (
                                <div style={styles.customizations}>
                                    {Object.entries(item.customizations).map(([key, val]) => (
                                        <span key={key}>{val} </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {order.specialInstructions && (
                <div style={styles.instructions}>
                    <span style={styles.noteIcon}>📝</span> {order.specialInstructions}
                </div>
            )}

            <div style={styles.divider}></div>

            <div style={styles.footer}>
                <div style={styles.totalSection}>
                    <span style={styles.totalLabel}>Total</span>
                    <span style={styles.totalAmount}>{formatCurrency(order.totalAmount)}</span>
                </div>

                <div style={styles.actions}>
                    {(order.status === 'RECEIVED' || order.status === 'PENDING') && (
                        <>
                            <button
                                style={{ ...styles.btn, ...styles.rejectBtn }}
                                onClick={handleReject}
                            >
                                Reject
                            </button>
                            <button
                                style={{ ...styles.btn, ...styles.acceptBtn }}
                                onClick={() => handleAction('ACCEPTED')}
                            >
                                Accept
                            </button>
                        </>
                    )}

                    {order.status === 'ACCEPTED' && (
                        <button
                            style={{ ...styles.btn, ...styles.primaryBtn }}
                            onClick={() => handleAction('PREPARING')}
                        >
                            Start Prep
                        </button>
                    )}

                    {order.status === 'PREPARING' && (
                        <button
                            style={{ ...styles.btn, ...styles.successBtn }}
                            onClick={() => handleAction('READY')}
                        >
                            Mark Ready
                        </button>
                    )}

                    {order.status === 'READY' && (
                        <button
                            style={{ ...styles.btn, ...styles.dispatchBtn }}
                            onClick={() => handleAction('DISPATCHED')}
                        >
                            Dispatch
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const getStyles = (isDark, status) => {
    const bg = isDark ? '#1E1E24' : '#FFFFFF';
    const text = isDark ? '#EAEAEA' : '#2D3436';
    const secondaryText = isDark ? '#A0A0A0' : '#888888';
    const borderColor = isDark ? '#333333' : '#F0F0F0';
    const accentColor = '#6C5CE7'; // Modern purple/indigo accent

    // Status Colors (Pastel/Modern palette)
    let statusBg = '#dfe6e9';
    let statusText = '#636e72';

    if (status === 'RECEIVED') { statusBg = 'rgba(52, 152, 219, 0.15)'; statusText = '#3498db'; }
    if (status === 'ACCEPTED') { statusBg = 'rgba(241, 196, 15, 0.15)'; statusText = '#f1c40f'; }
    if (status === 'PREPARING') { statusBg = 'rgba(230, 126, 34, 0.15)'; statusText = '#e67e22'; }
    if (status === 'READY') { statusBg = 'rgba(46, 204, 113, 0.15)'; statusText = '#2ecc71'; }
    if (status === 'DISPATCHED') { statusBg = 'rgba(155, 89, 182, 0.15)'; statusText = '#9b59b6'; }
    if (status === 'REJECTED' || status === 'CANCELLED') { statusBg = 'rgba(231, 76, 60, 0.15)'; statusText = '#e74c3c'; }

    return {
        card: {
            backgroundColor: bg,
            color: text,
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 20px rgba(0,0,0,0.05)',
            border: `1px solid ${borderColor}`,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            cursor: 'default'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
        },
        orderIdGroup: {
            display: 'flex',
            alignItems: 'center',
            gap: '2px'
        },
        hash: {
            color: secondaryText,
            fontSize: '1rem',
            fontWeight: '400'
        },
        orderNumber: {
            fontSize: '1.2rem',
            fontWeight: '700',
            letterSpacing: '0.5px'
        },
        time: {
            fontSize: '0.85rem',
            color: secondaryText,
            fontWeight: '500'
        },
        addressRow: {
            fontSize: '0.85rem',
            color: secondaryText,
            marginTop: '4px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        customerRow: {
            display: 'flex',
            alignItems: 'baseline',
            gap: '6px'
        },
        customerName: {
            fontSize: '1rem',
            fontWeight: '600'
        },
        customerPhone: {
            fontSize: '0.9rem',
            color: secondaryText
        },
        badges: {
            display: 'flex',
            gap: '8px',
            fontSize: '0.8rem',
            fontWeight: '600',
            flexWrap: 'wrap'
        },
        typeTag: {
            padding: '4px 10px',
            borderRadius: '20px',
            backgroundColor: isDark ? '#333' : '#F5F6FA',
            color: secondaryText,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontSize: '0.7rem'
        },
        statusTag: {
            padding: '4px 10px',
            borderRadius: '20px',
            backgroundColor: statusBg,
            color: statusText,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontSize: '0.7rem'
        },
        paymentTag: {
            padding: '4px 10px',
            borderRadius: '20px',
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        etaContainer: {
            backgroundColor: isDark ? 'rgba(230, 126, 34, 0.1)' : '#fff3e0',
            padding: '8px 12px',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: '1px solid #ffe0b2'
        },
        etaLabel: {
            fontSize: '0.85rem',
            fontWeight: '600',
            color: '#e67e22'
        },
        etaValue: {
            fontSize: '1rem',
            fontWeight: '700',
            color: '#d35400'
        },
        itemsContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '8px 0'
        },
        itemRow: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px'
        },
        quantityBadge: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '24px',
            height: '24px',
            borderRadius: '6px',
            backgroundColor: isDark ? '#333' : '#eee',
            color: text,
            fontSize: '0.8rem',
            fontWeight: '700'
        },
        itemDetails: {
            display: 'flex',
            flexDirection: 'column'
        },
        itemName: {
            fontSize: '0.95rem',
            fontWeight: '500'
        },
        customizations: {
            fontSize: '0.8rem',
            color: secondaryText,
            marginTop: '2px'
        },
        instructions: {
            backgroundColor: isDark ? 'rgba(255, 234, 167, 0.1)' : '#FFF9E5', // Subtle yellow tint
            color: isDark ? '#FFEAA7' : '#F39C12',
            padding: '10px',
            borderRadius: '8px',
            fontSize: '0.85rem',
            display: 'flex',
            gap: '6px',
            alignItems: 'flex-start',
            lineHeight: '1.4'
        },
        noteIcon: {
            fontSize: '1rem'
        },
        divider: {
            height: '1px',
            backgroundColor: borderColor,
            margin: '4px 0'
        },
        footer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        totalSection: {
            display: 'flex',
            flexDirection: 'column'
        },
        totalLabel: {
            fontSize: '0.75rem',
            color: secondaryText,
            textTransform: 'uppercase',
            fontWeight: '600'
        },
        totalAmount: {
            fontSize: '1.2rem',
            fontWeight: '800'
        },
        actions: {
            display: 'flex',
            gap: '8px'
        },
        btn: {
            padding: '10px 16px',
            borderRadius: '10px',
            border: 'none',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'transform 0.1s, opacity 0.2s',
            outline: 'none'
        },
        acceptBtn: {
            backgroundColor: '#00B894', // Minty green
            color: 'white'
        },
        rejectBtn: {
            backgroundColor: 'transparent',
            color: '#FF7675',
            border: '1px solid #FF7675' // Outline red
        },
        primaryBtn: {
            backgroundColor: '#0984E3', // Blue
            color: 'white'
        },
        successBtn: {
            backgroundColor: '#00B894',
            color: 'white'
        },
        dispatchBtn: {
            backgroundColor: '#6C5CE7', // Purple
            color: 'white'
        }
    };
};

export default OrderCard;
